import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { Banknote } from "lucide-react";

const COLORS = ["#C99A2E", "#145A7A", "#2F6B4F", "#A96A35", "#7CBFDC", "#6BB88A", "#B58BC2", "#E67E7E"];

export const Revenue = () => {
  const { data } = useQuery({ queryKey: ["revenue"], queryFn: async () => (await api.get("/revenue/ledger")).data });
  const items = data?.items || [];
  const breakdown = data?.breakdown || [];

  return (
    <div className="space-y-6" data-testid="page-revenue">
      <header>
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#7C93A5]">Revenue360™</div>
        <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1">National Revenue Ledger</h1>
        <div className="text-[13px] text-[#B3C2CC] mt-1">Fees · ground rental · royalties · penalties — reconciled against Treasury interface.</div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="cad-panel p-5 lg:col-span-1">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#7C93A5]">Fee mix</div>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={breakdown} dataKey="amount" nameKey="fee_type" outerRadius={95} innerRadius={55}>
                {breakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#0B3558", border: "1px solid #C99A2E", color: "#F6F8FA" }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1 mt-2">
            {breakdown.map((b, i) => (
              <div key={b.fee_type} className="flex items-center gap-2 text-[12px]">
                <div className="w-2.5 h-2.5" style={{ background: COLORS[i % COLORS.length] }}></div>
                <span className="text-[#DCE3E8] capitalize">{b.fee_type.replace(/_/g, " ")}</span>
                <span className="ml-auto font-mono-cad text-[#F6F8FA]">${Math.round(b.amount).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="cad-panel p-0 lg:col-span-2 overflow-hidden">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left border-b cad-hairline">
                {["Date","Reference","Fee Type","Payer","Amount USD","Status"].map((h) => (
                  <th key={h} className="px-3 py-2.5 font-mono-cad text-[10px] uppercase tracking-[0.18em] text-[#7C93A5]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="cad-tbody">
              {items.slice(0, 60).map((r) => (
                <tr key={r.id} data-testid={`revenue-row-${r.id}`}>
                  <td className="px-3 py-2 font-mono-cad text-[#B3C2CC]">{(r.date || "").slice(0, 10)}</td>
                  <td className="px-3 py-2 font-mono-cad text-[#F6F8FA]">{r.reference}</td>
                  <td className="px-3 py-2 capitalize">{r.fee_type.replace(/_/g, " ")}</td>
                  <td className="px-3 py-2 text-[#DCE3E8]">{r.payer}</td>
                  <td className="px-3 py-2 font-mono-cad text-[#F6F8FA]"><Banknote size={11} className="inline mr-1 text-[#C99A2E]" />${r.amount_usd.toLocaleString()}</td>
                  <td className="px-3 py-2">
                    <span className={`cad-chip ${r.status === "paid" ? "cad-chip-green" : r.status === "overdue" ? "cad-chip-red" : "cad-chip-gold"}`}>{r.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
